// frontend/src/screens/admin/WardPreShiftListScreen.jsx
import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle,
  Divider,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { toast } from 'react-toastify';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import { lightGreen, red, orange, blue, grey } from '@mui/material/colors';
import { useLayout } from '../../context/LayoutContext';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import HistoryIcon from '@mui/icons-material/History';
import AddIcon from '@mui/icons-material/Add';

import {
  useGetPreShiftsQuery,
  useApprovePreShiftMutation,
  useRejectPreShiftMutation,
  useDeletePreShiftMutation,
} from '../../slices/preShiftApiSlice';

import { useGetHospitalLocationsQuery } from '../../slices/hospitalLocationApiSlice';

// Styled DataGrid with conditional row styles
const StyledDataGrid = styled(DataGrid)`
  & .${gridClasses.row}.pending-row {
    background-color: ${orange[50]};
  }
  & .${gridClasses.row}.approved-row {
    background-color: ${lightGreen[100]};
  }
  & .${gridClasses.row}.rejected-row {
    background-color: ${red[100]};
  }
`;

// ─── Status → colour / icon lookup, shared by the grid row classes (desktop)
// and the card accent colour / chip (mobile) ────────────────────────────────
const STATUS_META = {
  Pending:  { chipColor: 'warning', icon: <HistoryIcon fontSize="small" />,      accent: orange[400],     rowClass: 'pending-row' },
  Approved: { chipColor: 'success', icon: <CheckCircleIcon fontSize="small" />,  accent: lightGreen[500], rowClass: 'approved-row' },
  Rejected: { chipColor: 'error',   icon: <CancelIcon fontSize="small" />,       accent: red[400],        rowClass: 'rejected-row' },
};
const getStatusMeta = (status) => STATUS_META[status] || { chipColor: 'default', icon: null, accent: grey[400], rowClass: '' };

// Helper function to extract MongoDB OID
const extractMongoId = (id) => {
  if (!id) return null;
  if (typeof id === 'object' && id.$oid) return id.$oid;
  if (typeof id === 'string') return id;
  if (typeof id === 'object' && id._id) return extractMongoId(id._id);
  return id?.toString() || null;
};

const WardPreShiftListScreen = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const { setFluid } = useLayout();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [filters, setFilters] = useState({
    directorate: '',
    wardDepartmentArea: '',
    categoryOfStaff: '',
    preShiftStatus: '',
    dateRange: {
      start: '',
      end: '',
    },
  });

  // ── Pagination state ───────────────────────────────────────────────────────
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  // State for modals
  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openApproveAllModal, setOpenApproveAllModal] = useState(false);
  const [selectedPreShift, setSelectedPreShift] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [showFilters, setShowFilters] = useState(true);

  // ── Approve-All progress state ─────────────────────────────────────────────
  const [approveAllProgress, setApproveAllProgress] = useState({ running: false, done: 0, total: 0, errors: 0 });

  // API hooks
  const { data: preShifts, isLoading, isError, error, refetch } = useGetPreShiftsQuery();
  const { data: hospitalLocations, isLoading: loadingLocations } = useGetHospitalLocationsQuery();

  const [approvePreShift, { isLoading: approving }] = useApprovePreShiftMutation();
  const [rejectPreShift, { isLoading: rejecting }] = useRejectPreShiftMutation();
  const [deletePreShift, { isLoading: deleting }] = useDeletePreShiftMutation();

  // Create a map of hospital location IDs to their data
  const hospitalLocationMap = useMemo(() => {
    if (!hospitalLocations) return {};
    const map = {};
    hospitalLocations.forEach(location => {
      const locationId = extractMongoId(location._id);
      if (locationId) {
        map[locationId] = {
          directorate: location.directorate,
          department: location.department,
          wardDepartmentArea: location.wardDepartmentArea,
          costCentreCode: location.costCentreCode,
        };
      }
    });
    return map;
  }, [hospitalLocations]);

  // Enrich pre-shifts with location data
  const enrichedPreShifts = useMemo(() => {
    if (!preShifts || !hospitalLocations) return [];

    return preShifts.map(preShift => {
      const locationId = extractMongoId(preShift.hospitalLocationId);
      const locationData = locationId ? hospitalLocationMap[locationId] : null;

      return {
        ...preShift,
        directorate: locationData?.directorate || preShift.directorate || 'N/A',
        department: locationData?.department || 'N/A',
        displayWardDepartmentArea: preShift.wardDepartmentArea || locationData?.wardDepartmentArea || 'N/A',
        requestedDate: new Date(preShift.createdAt).toLocaleDateString(),
        shiftDate: new Date(preShift.startDateTime).toLocaleDateString(),
        shiftTime: `${new Date(preShift.startDateTime).toLocaleTimeString()} - ${new Date(preShift.endDateTime).toLocaleTimeString()}`,
      };
    });
  }, [preShifts, hospitalLocations, hospitalLocationMap]);

  // Extract unique filter options
  const filterOptions = useMemo(() => {
    if (!enrichedPreShifts || enrichedPreShifts.length === 0) {
      return { directorates: [], wards: [], categories: [], statuses: [] };
    }

    return {
      directorates: [...new Set(
        enrichedPreShifts
          .map(shift => shift.directorate)
          .filter(dir => dir && dir !== 'N/A')
      )].sort(),
      wards: [...new Set(
        enrichedPreShifts
          .map(shift => shift.displayWardDepartmentArea)
          .filter(ward => ward && ward !== 'N/A')
      )].sort(),
      categories: [...new Set(
        enrichedPreShifts
          .map(shift => shift.categoryOfStaff)
          .filter(cat => cat)
      )].sort(),
      statuses: [...new Set(
        enrichedPreShifts
          .map(shift => shift.preShiftStatus)
          .filter(status => status)
      )].sort(),
    };
  }, [enrichedPreShifts]);

  // Filter pre-shifts
  const filteredPreShifts = useMemo(() => {
    if (!enrichedPreShifts) return [];

    return enrichedPreShifts.filter(preShift => {
      if (filters.directorate && preShift.directorate !== filters.directorate) return false;
      if (filters.wardDepartmentArea && preShift.displayWardDepartmentArea !== filters.wardDepartmentArea) return false;
      if (filters.categoryOfStaff && preShift.categoryOfStaff !== filters.categoryOfStaff) return false;
      if (filters.preShiftStatus && preShift.preShiftStatus !== filters.preShiftStatus) return false;

      if (filters.dateRange.start) {
        const startDate = new Date(filters.dateRange.start);
        const shiftStartDate = new Date(preShift.startDateTime);
        if (shiftStartDate < startDate) return false;
      }

      if (filters.dateRange.end) {
        const endDate = new Date(filters.dateRange.end);
        const shiftEndDate = new Date(preShift.endDateTime);
        if (shiftEndDate > endDate) return false;
      }

      return true;
    });
  }, [enrichedPreShifts, filters]);

  // ── Pending items within the current filtered view ─────────────────────────
  const filteredPendingPreShifts = useMemo(
    () => filteredPreShifts.filter(ps => ps.preShiftStatus === 'Pending'),
    [filteredPreShifts]
  );

  // ── Status breakdown: count + % for each status in the filtered list ───────
  const statusBreakdown = useMemo(() => {
    const total = filteredPreShifts.length;
    if (total === 0) return [];

    const counts = filteredPreShifts.reduce((acc, ps) => {
      const s = ps.preShiftStatus || 'Unknown';
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    }, {});

    const ORDER = ['Pending', 'Approved', 'Rejected'];
    const COLOR = {
      Pending:  { bg: orange[400],      text: '#fff' },
      Approved: { bg: lightGreen[500],  text: '#fff' },
      Rejected: { bg: red[400],         text: '#fff' },
      Unknown:  { bg: grey[400],        text: '#fff' },
    };

    // Show known statuses in fixed order, then any unknown ones
    const keys = [
      ...ORDER.filter(s => counts[s]),
      ...Object.keys(counts).filter(s => !ORDER.includes(s)),
    ];

    return keys.map(status => ({
      status,
      count: counts[status],
      pct: Math.round((counts[status] / total) * 100),
      ...COLOR[status] || COLOR.Unknown,
    }));
  }, [filteredPreShifts]);

  const isFilterActive = useMemo(() => {
    return Object.entries(filters).some(([key, value]) => {
      if (key === 'dateRange') return value.start || value.end;
      return Boolean(value);
    });
  }, [filters]);

  const clearFilters = () => {
    setFilters({
      directorate: '',
      wardDepartmentArea: '',
      categoryOfStaff: '',
      preShiftStatus: '',
      dateRange: { start: '', end: '' },
    });
  };

  const handleFilterChange = (filterName, value) => {
    // Reset to page 0 whenever filters change
    setPaginationModel(prev => ({ ...prev, page: 0 }));

    if (filterName.includes('dateRange')) {
      const [range, type] = filterName.split('.');
      setFilters(prev => ({
        ...prev,
        dateRange: {
          ...prev.dateRange,
          [type]: value,
        },
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [filterName]: value,
      }));
    }
  };

  // Column definitions
  const columns = [
    {
      field: 'requestedDate',
      headerName: 'Requested',
      width: 100,
    },
    {
      field: 'shiftDate',
      headerName: 'Shift Date',
      width: 100,
    },
    {
      field: 'shiftTime',
      headerName: 'Shift Time',
      width: 180,
    },
    {
      field: 'displayWardDepartmentArea',
      headerName: 'Ward/Dept Area',
      width: 150,
    },
    {
      field: 'directorate',
      headerName: 'Directorate',
      width: 150,
    },
    {
      field: 'categoryOfStaff',
      headerName: 'Staff Category',
      width: 120
    },
    {
      field: 'reasonForRequest',
      headerName: 'Reason',
      width: 120
    },
    {
      field: 'preShiftStatus',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => {
        const status = params.value;
        const meta = getStatusMeta(status);
        return (
          <Chip
            label={status}
            color={meta.chipColor}
            size="small"
            icon={meta.icon}
          />
        );
      }
    },
    {
      field: 'requestedByName',
      headerName: 'Requested By',
      width: 120,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              color="info"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPreShift(params.row);
                setOpenViewModal(true);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {params.row.preShiftStatus === 'Pending' && (
            <>
              <Tooltip title="Approve">
                <IconButton
                  size="small"
                  color="success"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPreShift(params.row);
                    setOpenApproveModal(true);
                  }}
                >
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Reject">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPreShift(params.row);
                    setOpenRejectModal(true);
                  }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPreShift(params.row);
                    setOpenDeleteModal(true);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}

          {params.row.preShiftStatus === 'Approved' && params.row.createdShiftId && (
            <Tooltip title="View Created Shift">
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/shifts/${params.row.createdShiftId}/edit`);
                }}
              >
                View Shift
              </Button>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  // Row styling function (desktop grid)
  const getRowClassName = (params) => getStatusMeta(params.row.preShiftStatus).rowClass;

  // Handle approve (single)
  const handleApprove = async () => {
    try {
      await approvePreShift({
        id: selectedPreShift._id,
        approvalNotes,
      }).unwrap();

      toast.success('Pre-shift approved and shift created successfully!');
      setOpenApproveModal(false);
      setSelectedPreShift(null);
      setApprovalNotes('');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to approve pre-shift');
    }
  };

  // ── Handle Approve All ─────────────────────────────────────────────────────
  const handleApproveAll = async () => {
    const pending = filteredPendingPreShifts;
    if (!pending.length) return;

    setApproveAllProgress({ running: true, done: 0, total: pending.length, errors: 0 });

    let errors = 0;
    for (let i = 0; i < pending.length; i++) {
      try {
        await approvePreShift({ id: pending[i]._id, approvalNotes: '' }).unwrap();
      } catch {
        errors += 1;
      }
      // Update progress after each attempt
      setApproveAllProgress(prev => ({ ...prev, done: i + 1, errors }));
    }

    setApproveAllProgress(prev => ({ ...prev, running: false }));

    if (errors === 0) {
      toast.success(`All ${pending.length} pre-shift${pending.length > 1 ? 's' : ''} approved successfully!`);
    } else {
      toast.warning(`Approved ${pending.length - errors} of ${pending.length}. ${errors} failed.`);
    }

    setOpenApproveAllModal(false);
    setApproveAllProgress({ running: false, done: 0, total: 0, errors: 0 });
    refetch();
  };

  // Handle reject
  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      await rejectPreShift({
        id: selectedPreShift._id,
        rejectionReason,
      }).unwrap();

      toast.success('Pre-shift rejected successfully!');
      setOpenRejectModal(false);
      setSelectedPreShift(null);
      setRejectionReason('');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to reject pre-shift');
    }
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      await deletePreShift(selectedPreShift._id).unwrap();
      toast.success('Pre-shift deleted successfully!');
      setOpenDeleteModal(false);
      setSelectedPreShift(null);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to delete pre-shift');
    }
  };

  useEffect(() => {
    setFluid(true);
    return () => setFluid(false);
  }, [setFluid]);

  if (isLoading || loadingLocations) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Typography color="error">Error: {error.message}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: isMobile ? 'auto' : 'calc(100vh - 100px)', width: '100%', p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', mb: 2, gap: 1 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/ward-preshifts/create')}
          fullWidth={isMobile}
        >
          Create Pre-Shift Request
        </Button>

        <Box sx={{ display: 'flex', gap: 1, width: isMobile ? '100%' : 'auto' }}>
          <Tooltip title="Toggle Filters">
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
              fullWidth={isMobile}
            >
              Filters {isFilterActive && (
                <Chip
                  size="small"
                  label="Active"
                  color="primary"
                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Filter Panel */}
      {showFilters && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Directorate</InputLabel>
                <Select
                  value={filters.directorate}
                  label="Directorate"
                  onChange={(e) => handleFilterChange('directorate', e.target.value)}
                >
                  <MenuItem value="">All Directorates</MenuItem>
                  {filterOptions.directorates.map(dir => (
                    <MenuItem key={dir} value={dir}>{dir}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Ward/Dept Area</InputLabel>
                <Select
                  value={filters.wardDepartmentArea}
                  label="Ward/Dept Area"
                  onChange={(e) => handleFilterChange('wardDepartmentArea', e.target.value)}
                >
                  <MenuItem value="">All Areas</MenuItem>
                  {filterOptions.wards.map(ward => (
                    <MenuItem key={ward} value={ward}>{ward}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Staff Category</InputLabel>
                <Select
                  value={filters.categoryOfStaff}
                  label="Staff Category"
                  onChange={(e) => handleFilterChange('categoryOfStaff', e.target.value)}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {filterOptions.categories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.preShiftStatus}
                  label="Status"
                  onChange={(e) => handleFilterChange('preShiftStatus', e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {filterOptions.statuses.map(status => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="date"
                value={filters.dateRange.start}
                onChange={(e) => handleFilterChange('dateRange.start', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="End Date"
                type="date"
                value={filters.dateRange.end}
                onChange={(e) => handleFilterChange('dateRange.end', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  disabled={!isFilterActive}
                >
                  Clear Filters
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setShowFilters(false)}
                >
                  Apply
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Results Summary + Approve All */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="subtitle1" color="textSecondary">
          Showing {filteredPreShifts.length} of {preShifts?.length || 0} pre-shift requests
          {isFilterActive && (
            <Chip
              label={`${filteredPreShifts.length} filtered`}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ ml: 1 }}
            />
          )}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: isMobile ? '100%' : 'auto' }}>
          {/* ── Approve All button: only shown when there are pending items in view ── */}
          {filteredPendingPreShifts.length > 0 && (
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<DoneAllIcon />}
              onClick={() => setOpenApproveAllModal(true)}
              fullWidth={isMobile}
            >
              Approve All Pending ({filteredPendingPreShifts.length})
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Status breakdown strip ─────────────────────────────────────────── */}
      {filteredPreShifts.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
          {/* Segmented progress bar */}
          <Box sx={{ display: 'flex', height: 10 }}>
            {statusBreakdown.map(({ status, pct, bg }) => (
              <Tooltip key={status} title={`${status}: ${pct}%`} arrow>
                <Box
                  sx={{
                    width: `${pct}%`,
                    bgcolor: bg,
                    transition: 'width 0.4s ease',
                    '&:first-of-type': { borderRadius: '8px 0 0 0' },
                    '&:last-of-type':  { borderRadius: '0 8px 0 0' },
                  }}
                />
              </Tooltip>
            ))}
          </Box>

          {/* Stat cards row */}
          <Box sx={{ display: 'flex', flexWrap: isMobile ? 'wrap' : 'nowrap', divideX: 1 }}>
            {statusBreakdown.map(({ status, count, pct, bg }, idx) => (
              <Box
                key={status}
                sx={{
                  flex: isMobile ? '1 1 50%' : 1,
                  px: 2,
                  py: 1,
                  borderRight: !isMobile && idx < statusBreakdown.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                {/* Colour dot */}
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: bg, flexShrink: 0 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                    {status}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                      {count}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({pct}%)
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}

            {/* Total — always on the right */}
            <Box
              sx={{
                flex: isMobile ? '1 1 100%' : '0 0 auto',
                px: 2,
                py: 1,
                borderLeft: !isMobile ? '1px solid' : 'none',
                borderTop: isMobile ? '1px solid' : 'none',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                bgcolor: 'grey.50',
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                  Total
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                  {filteredPreShifts.length}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Main Content */}
      <Card elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <CardHeader
          title="Pre-Shift Requests"
          subheader="Review and approve shift requests from ward managers"
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            '& .MuiCardHeader-subheader': { color: 'rgba(255,255,255,0.7)' },
          }}
        />

        {isMobile ? (
          /* ── Mobile card list ── */
          <Box sx={{ p: 1.5, bgcolor: 'grey.50' }}>
            <Stack spacing={1.5}>
              {filteredPreShifts.map((preShift) => {
                const meta = getStatusMeta(preShift.preShiftStatus);
                const rowId = extractMongoId(preShift._id) || preShift._id;
                return (
                  <Card
                    key={rowId}
                    elevation={2}
                    sx={{ borderLeft: `6px solid ${meta.accent}` }}
                  >
                    <CardContent
                      sx={{ pb: 1, cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedPreShift(preShift);
                        setOpenViewModal(true);
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {preShift.displayWardDepartmentArea}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {preShift.directorate}
                          </Typography>
                        </Box>
                        <Chip label={preShift.preShiftStatus} color={meta.chipColor} size="small" icon={meta.icon} />
                      </Box>

                      <Typography variant="body2" color="textSecondary">
                        <strong>Shift:</strong> {preShift.shiftDate} · {preShift.shiftTime}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Requested:</strong> {preShift.requestedDate} by {preShift.requestedByName || 'Unknown'}
                      </Typography>

                      <Divider sx={{ my: 1 }} />

                      <Typography variant="body2">
                        <strong>Staff Category:</strong> {preShift.categoryOfStaff || '—'}
                      </Typography>
                      {preShift.reasonForRequest && (
                        <Typography variant="body2">
                          <strong>Reason:</strong> {preShift.reasonForRequest}
                        </Typography>
                      )}
                    </CardContent>

                    <CardActions sx={{ justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1, px: 2, pb: 1.5 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon fontSize="small" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPreShift(preShift);
                          setOpenViewModal(true);
                        }}
                      >
                        View
                      </Button>

                      {preShift.preShiftStatus === 'Pending' && (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<CancelIcon fontSize="small" />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPreShift(preShift);
                              setOpenRejectModal(true);
                            }}
                          >
                            Reject
                          </Button>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPreShift(preShift);
                              setOpenDeleteModal(true);
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            fullWidth
                            startIcon={<CheckCircleIcon fontSize="small" />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPreShift(preShift);
                              setOpenApproveModal(true);
                            }}
                          >
                            Approve
                          </Button>
                        </>
                      )}

                      {preShift.preShiftStatus === 'Approved' && preShift.createdShiftId && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          fullWidth
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/shifts/${preShift.createdShiftId}/edit`);
                          }}
                        >
                          View Shift
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                );
              })}
            </Stack>
          </Box>
        ) : (
          /* Data Grid — pagination re-enabled */
          <Box sx={{ height: 'calc(100vh - 380px)', width: '100%' }}>
            <StyledDataGrid
              density="compact"
              rows={filteredPreShifts}
              columns={columns}
              getRowId={(row) => extractMongoId(row._id) || row._id}
              getRowClassName={getRowClassName}

              // ── Pagination ───────────────────────────────────────────────────
              pagination
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50, 100]}

              disableSelectionOnClick
              disableColumnMenu
              disableDensitySelector
              onRowClick={(params) => {
                setSelectedPreShift(params.row);
                setOpenViewModal(true);
              }}
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #f0f0f0',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  cursor: 'pointer',
                },
                // Keep the footer (pagination) always visible
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '1px solid #e0e0e0',
                  bgcolor: 'grey.50',
                },
              }}
            />
          </Box>
        )}

        {filteredPreShifts.length === 0 && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 200,
            color: 'text.secondary',
            flexDirection: 'column',
            gap: 1,
          }}>
            <Typography variant="h6">No pre-shift requests found</Typography>
            <Typography variant="body2">
              {isFilterActive
                ? "No requests match your current filters. Try adjusting or clearing filters."
                : "No pre-shift requests available. Create a new request to get started."}
            </Typography>
            {isFilterActive && (
              <Button
                variant="outlined"
                size="small"
                onClick={clearFilters}
                startIcon={<ClearIcon />}
              >
                Clear All Filters
              </Button>
            )}
          </Box>
        )}

        <Box sx={{
          px: 2,
          py: 0.75,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'grey.50',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Typography variant="caption" color="textSecondary">
            Total: {filteredPreShifts.length} requests
          </Typography>
          {!isMobile && (
            <Typography variant="caption" color="textSecondary">
              Click any row to view details
            </Typography>
          )}
        </Box>
      </Card>

      {/* ── Approve All Confirmation Modal ────────────────────────────────── */}
      <Dialog open={openApproveAllModal} onClose={() => !approveAllProgress.running && setOpenApproveAllModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Approve All Pending Requests
          {!approveAllProgress.running && (
            <IconButton
              aria-label="close"
              onClick={() => setOpenApproveAllModal(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {!approveAllProgress.running ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Bulk Approval</AlertTitle>
                This will approve <strong>{filteredPendingPreShifts.length}</strong> pending pre-shift request{filteredPendingPreShifts.length > 1 ? 's' : ''} and create a corresponding shift for each one. This cannot be undone.
              </Alert>

              {isFilterActive && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Only the <strong>{filteredPendingPreShifts.length}</strong> pending items matching your current filters will be approved — not the entire list.
                </Alert>
              )}

              <Typography variant="body2" color="textSecondary">
                Breakdown of items to approve:
              </Typography>
              <Box sx={{ mt: 1, pl: 1 }}>
                {Object.entries(
                  filteredPendingPreShifts.reduce((acc, ps) => {
                    const key = ps.displayWardDepartmentArea || 'Unknown';
                    acc[key] = (acc[key] || 0) + 1;
                    return acc;
                  }, {})
                ).map(([ward, count]) => (
                  <Typography key={ward} variant="body2">
                    • {ward}: <strong>{count}</strong>
                  </Typography>
                ))}
              </Box>
            </Box>
          ) : (
            <Box>
              <Typography variant="body1" gutterBottom>
                Approving {approveAllProgress.done} of {approveAllProgress.total}…
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(approveAllProgress.done / approveAllProgress.total) * 100}
                sx={{ height: 10, borderRadius: 5, my: 2 }}
              />
              {approveAllProgress.errors > 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {approveAllProgress.errors} request{approveAllProgress.errors > 1 ? 's' : ''} failed so far.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        {!approveAllProgress.running && (
          <DialogActions>
            <Button onClick={() => setOpenApproveAllModal(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<DoneAllIcon />}
              onClick={handleApproveAll}
            >
              Approve All ({filteredPendingPreShifts.length})
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* View Details Modal */}
      <Dialog open={openViewModal} onClose={() => setOpenViewModal(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Pre-Shift Request Details
          <IconButton
            aria-label="close"
            onClick={() => setOpenViewModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPreShift && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Request Status
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Chip
                      label={selectedPreShift.preShiftStatus}
                      color={selectedPreShift.preShiftStatus === 'Pending' ? 'warning' :
                             selectedPreShift.preShiftStatus === 'Approved' ? 'success' : 'error'}
                      icon={selectedPreShift.preShiftStatus === 'Pending' ? <HistoryIcon /> :
                            selectedPreShift.preShiftStatus === 'Approved' ? <CheckCircleIcon /> : <CancelIcon />}
                    />
                    <Typography variant="body2">
                      Requested on: {new Date(selectedPreShift.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="primary">Shift Details</Typography>
                <Typography><strong>Start:</strong> {new Date(selectedPreShift.startDateTime).toLocaleString()}</Typography>
                <Typography><strong>End:</strong> {new Date(selectedPreShift.endDateTime).toLocaleString()}</Typography>
                <Typography><strong>Category:</strong> {selectedPreShift.categoryOfStaff}</Typography>
                <Typography><strong>Reason:</strong> {selectedPreShift.reasonForRequest}</Typography>
                    {selectedPreShift.specialsReason && (
                      <Typography>
                        <strong>Specials / Cohorts Reason:</strong> {selectedPreShift.specialsReason}
                      </Typography>
                    )}
                    <Typography><strong>Details:</strong> {selectedPreShift.reasonForRequestDetails}</Typography>
                {selectedPreShift.specialRequirements && (
                  <Typography><strong>Special Req:</strong> {selectedPreShift.specialRequirements}</Typography>
                )}
                {selectedPreShift.comments && (
                  <Typography><strong>Comments:</strong> {selectedPreShift.comments}</Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="primary">Location Details</Typography>
                <Typography><strong>Directorate:</strong> {selectedPreShift.directorate}</Typography>
                <Typography><strong>Ward/Dept:</strong> {selectedPreShift.displayWardDepartmentArea}</Typography>
                <Typography><strong>Department:</strong> {selectedPreShift.department}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="primary">Requestor Details</Typography>
                <Typography><strong>Name:</strong> {selectedPreShift.requestedByName}</Typography>
                <Typography><strong>Email:</strong> {selectedPreShift.requestedByEmail}</Typography>
              </Grid>

              {selectedPreShift.preShiftStatus !== 'Pending' && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="primary">Approval Details</Typography>
                  <Typography><strong>Approved By:</strong> {selectedPreShift.approvedByName || 'N/A'}</Typography>
                  <Typography><strong>Approved At:</strong> {selectedPreShift.approvedAt ? new Date(selectedPreShift.approvedAt).toLocaleString() : 'N/A'}</Typography>
                  {selectedPreShift.rejectionReason && (
                    <Typography><strong>Rejection Reason:</strong> {selectedPreShift.rejectionReason}</Typography>
                  )}
                  {selectedPreShift.approvalNotes && (
                    <Typography><strong>Approval Notes:</strong> {selectedPreShift.approvalNotes}</Typography>
                  )}
                </Grid>
              )}

              {selectedPreShift.preShiftStatus === 'Approved' && selectedPreShift.createdShiftId && (
                <Grid item xs={12}>
                  <Alert severity="success">
                    <AlertTitle>Shift Created</AlertTitle>
                    This request has been approved and a shift has been created.
                    <Button
                      size="small"
                      sx={{ ml: 2 }}
                      variant="outlined"
                      onClick={() => {
                        setOpenViewModal(false);
                        navigate(`/admin/shifts/${selectedPreShift.createdShiftId}/edit`);
                      }}
                    >
                      View Created Shift
                    </Button>
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewModal(false)}>Close</Button>
          {selectedPreShift?.preShiftStatus === 'Pending' && (
            <>
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  setOpenViewModal(false);
                  setOpenApproveModal(true);
                }}
              >
                Approve
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  setOpenViewModal(false);
                  setOpenRejectModal(true);
                }}
              >
                Reject
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Approve Modal */}
      <Dialog open={openApproveModal} onClose={() => setOpenApproveModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Approve Pre-Shift Request
          <IconButton
            aria-label="close"
            onClick={() => setOpenApproveModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPreShift && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>Confirm Approval</AlertTitle>
                This action will create an actual shift from this request.
              </Alert>

              <Typography variant="body2" gutterBottom>
                <strong>Shift:</strong> {new Date(selectedPreShift.startDateTime).toLocaleString()} - {new Date(selectedPreShift.endDateTime).toLocaleString()}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Ward:</strong> {selectedPreShift.displayWardDepartmentArea}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Staff Category:</strong> {selectedPreShift.categoryOfStaff}
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={3}
                margin="normal"
                label="Approval Notes (Optional)"
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder="Add any notes about this approval..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenApproveModal(false);
            setApprovalNotes('');
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApprove}
            disabled={approving}
          >
            {approving ? <CircularProgress size={24} /> : 'Confirm Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={openRejectModal} onClose={() => setOpenRejectModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Reject Pre-Shift Request
          <IconButton
            aria-label="close"
            onClick={() => setOpenRejectModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPreShift && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Confirm Rejection</AlertTitle>
                This request will be marked as rejected and no shift will be created.
              </Alert>

              <Typography variant="body2" gutterBottom>
                <strong>Shift:</strong> {new Date(selectedPreShift.startDateTime).toLocaleString()} - {new Date(selectedPreShift.endDateTime).toLocaleString()}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Ward:</strong> {selectedPreShift.displayWardDepartmentArea}
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={3}
                margin="normal"
                label="Rejection Reason *"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                error={!rejectionReason.trim()}
                helperText={!rejectionReason.trim() ? 'Rejection reason is required' : ''}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenRejectModal(false);
            setRejectionReason('');
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={rejecting || !rejectionReason.trim()}
          >
            {rejecting ? <CircularProgress size={24} /> : 'Confirm Rejection'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Delete Pre-Shift Request
          <IconButton
            aria-label="close"
            onClick={() => setOpenDeleteModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="error">
            <AlertTitle>Warning</AlertTitle>
            Are you sure you want to delete this pre-shift request? This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WardPreShiftListScreen;